import React, { useState, useEffect } from 'react'

function FlightBooking() {
    const [tripType, setTripType] = useState('round-trip')
    const [passengers, setPassengers] = useState({
        adults: 1,
        children: 0,
        infants: 0
    })
    const [showPassengerDropdown, setShowPassengerDropdown] = useState(false)
    const [classType, setClassType] = useState('economy')
    const [destinations, setDestinations] = useState([])
    const [loading, setLoading] = useState(true)
    const [fromDestination, setFromDestination] = useState('')
    const [toDestination, setToDestination] = useState('')

    useEffect(() => {
        const fetchDestinations = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/flight-destinations/`)
                if (!response.ok) {
                    throw new Error('Failed to fetch destinations')
                }
                const data = await response.json()
                setDestinations(data.results || [])
                setLoading(false)
            } catch (err) {
                console.error('Error fetching destinations:', err)
                setLoading(false)
            }
        }

        fetchDestinations()
    }, [])

    const handlePassengerChange = (type, operation) => {
        setPassengers(prev => ({
            ...prev,
            [type]: operation === 'increase' ? prev[type] + 1 : Math.max(0, prev[type] - 1)
        }))
    }

    const totalPassengers = passengers.adults + passengers.children + passengers.infants

    const handleSubmit = (e) => {
        e.preventDefault()
        // Handle flight search logic here
        console.log('Search flights:', {
            tripType,
            from: fromDestination,
            to: toDestination,
            passengers,
            classType
        })
    }

    return (
        <div className="flight-booking-wrap py-5" style={{ marginTop: '-100px', position: 'relative', zIndex: 2 }}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12">
                        <div className="flight-booking-form bg-white p-4 rounded shadow-lg">
                            {/* Trip Type Selection */}
                            <div className="trip-type-wrap mb-4">
                                <div className="trip-type-buttons">
                                    <button 
                                        className={`btn me-3 ${tripType === 'one-way' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setTripType('one-way')}
                                    >
                                        <i className="fas fa-long-arrow-alt-right me-2"></i>
                                        One Way
                                    </button>
                                    <button 
                                        className={`btn me-3 ${tripType === 'round-trip' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setTripType('round-trip')}
                                    >
                                        <i className="fas fa-exchange-alt me-2"></i>
                                        Round Trip
                                    </button>
                                    <button 
                                        className={`btn ${tripType === 'multi-city' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setTripType('multi-city')}
                                    >
                                        <i className="fas fa-map-marker-alt me-2"></i>
                                        Multi City
                                    </button>
                                </div>
                            </div>

                            {/* Search Form */}
                            <form className="flight-search-form" onSubmit={handleSubmit}>
                                <div className="row g-3">
                                    {/* From */}
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">
                                            <i className="fas fa-plane-departure me-2"></i>From
                                        </label>
                                        <select 
                                            className="form-select form-select-lg"
                                            value={fromDestination}
                                            onChange={(e) => setFromDestination(e.target.value)}
                                            disabled={loading}
                                        >
                                            <option value="">Select departure city</option>
                                            {destinations.map((dest) => (
                                                <option key={dest.id} value={dest.code}>
                                                    {dest.city} ({dest.code})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* To */}
                                    <div className="col-md-3">
                                        <label className="form-label fw-bold">
                                            <i className="fas fa-plane-arrival me-2"></i>To
                                        </label>
                                        <select 
                                            className="form-select form-select-lg"
                                            value={toDestination}
                                            onChange={(e) => setToDestination(e.target.value)}
                                            disabled={loading}
                                        >
                                            <option value="">Select destination city</option>
                                            {destinations.map((dest) => (
                                                <option key={dest.id} value={dest.code}>
                                                    {dest.city} ({dest.code})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Departure Date */}
                                    <div className="col-md-2">
                                        <label className="form-label fw-bold">
                                            <i className="fas fa-calendar-alt me-2"></i>Departure
                                        </label>
                                        <input 
                                            type="date" 
                                            className="form-control form-control-lg" 
                                            defaultValue={new Date().toISOString().split('T')[0]}
                                        />
                                    </div>

                                    {/* Return Date */}
                                    {tripType === 'round-trip' && (
                                        <div className="col-md-2">
                                            <label className="form-label fw-bold">
                                                <i className="fas fa-calendar-alt me-2"></i>Return
                                            </label>
                                            <input 
                                                type="date" 
                                                className="form-control form-control-lg"
                                                defaultValue={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                            />
                                        </div>
                                    )}

                                    {/* Passengers */}
                                    <div className={`col-md-${tripType === 'round-trip' ? '2' : '4'} position-relative`}>
                                        <label className="form-label fw-bold">
                                            <i className="fas fa-users me-2"></i>Passengers & Class
                                        </label>
                                        <div 
                                            className="form-control form-control-lg d-flex justify-content-between align-items-center cursor-pointer"
                                            onClick={() => setShowPassengerDropdown(!showPassengerDropdown)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span>{totalPassengers} Passenger{totalPassengers > 1 ? 's' : ''}, {classType.charAt(0).toUpperCase() + classType.slice(1)}</span>
                                            <i className="fas fa-chevron-down"></i>
                                        </div>

                                        {/* Passenger Dropdown */}
                                        {showPassengerDropdown && (
                                            <div className="passenger-dropdown position-absolute bg-white border rounded shadow p-3 mt-1 w-100" style={{ zIndex: 1000 }}>
                                                {/* Adults */}
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <div>
                                                        <div className="fw-bold">Adults</div>
                                                        <small className="text-muted">12+ years</small>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => handlePassengerChange('adults', 'decrease')}
                                                            disabled={passengers.adults <= 1}
                                                        >
                                                            -
                                                        </button>
                                                        <span className="mx-3">{passengers.adults}</span>
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => handlePassengerChange('adults', 'increase')}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Children */}
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <div>
                                                        <div className="fw-bold">Children</div>
                                                        <small className="text-muted">2-11 years</small>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => handlePassengerChange('children', 'decrease')}
                                                            disabled={passengers.children <= 0}
                                                        >
                                                            -
                                                        </button>
                                                        <span className="mx-3">{passengers.children}</span>
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => handlePassengerChange('children', 'increase')}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Infants */}
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <div>
                                                        <div className="fw-bold">Infants</div>
                                                        <small className="text-muted">Under 2 years</small>
                                                    </div>
                                                    <div className="d-flex align-items-center">
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => handlePassengerChange('infants', 'decrease')}
                                                            disabled={passengers.infants <= 0}
                                                        >
                                                            -
                                                        </button>
                                                        <span className="mx-3">{passengers.infants}</span>
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-secondary btn-sm"
                                                            onClick={() => handlePassengerChange('infants', 'increase')}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Class Selection */}
                                                <div className="mb-3">
                                                    <label className="form-label fw-bold">Class</label>
                                                    <select 
                                                        className="form-select"
                                                        value={classType}
                                                        onChange={(e) => setClassType(e.target.value)}
                                                    >
                                                        <option value="economy">Economy</option>
                                                        <option value="premium-economy">Premium Economy</option>
                                                        <option value="business">Business</option>
                                                        <option value="first">First Class</option>
                                                    </select>
                                                </div>

                                                <button 
                                                    type="button" 
                                                    className="btn btn-primary btn-sm w-100"
                                                    onClick={() => setShowPassengerDropdown(false)}
                                                >
                                                    Done
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Search Button */}
                                <div className="row mt-4">
                                    <div className="col-12 text-center">
                                        <button type="submit" className="btn btn-primary btn-lg px-5 py-3">
                                            <i className="fas fa-search me-2"></i>
                                            Search Flights
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default FlightBooking
